/**
 * PlanFinanceSections
 *
 * The Quotes and Payments cards on a patient's Plans tab, under Plan Details.
 * They mirror the two lists legacy's plan page shows for the open plan
 * (html/html/plan_details-test.php:3639-3775): quotes from
 * lib/tables/dt-plan-quote-list.php and payments from
 * lib/tables/dt-plan-payments-list.php, each filtered to that plan.
 *
 * Each card follows the patient's own tab: the caller passes `enabled` from the
 * same visibility rule that shows or hides the Quotes and Payments tabs, and a
 * disabled card sends no request. Unlike the other plan cards, these two show
 * once their list has loaded even when it is empty: each carries the add
 * button (Add Quote, Add Payment) that attaches a new row to this plan, so
 * hiding an empty card meant a plan could never get its first quote or
 * payment from here. The chrome is the sibling cards'
 * (PlanAppointmentsSection in PlansTab.tsx), copied rather than shared so each card stays readable alone.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, DollarSign, FileText, Gift, Plus } from 'lucide-react';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import { PaymentStatusBadge } from '@/components/payments/PaymentStatusBadge';
import { createQuote, getQuotesByPatient, type PatientQuoteRow } from '@/services/quotes.service';
import { getPayments } from '@/services/payments.service';
import { getErrorMessage } from '@/services/apiClient';
import { listUsers, type AdminUser } from '@/services/admin.service';
import { formatPaymentDate, paymentTypeLabel } from '@/pages/payments/paymentsShared';
import { ChargePaymentModal } from '@/pages/payments/ChargePaymentModal';
import { RecordPatientPaymentModal } from '@/pages/payments/RecordPatientPaymentModal';
import { DirectPaymentLinkModal } from '@/pages/payments/DirectPaymentLinkModal';
import QuoteEditorModal from '@/pages/quotes/QuoteEditorModal';
import { toast } from '@/store/toastStore';

/** Rows the Payments card asks for; the server caps a page at 200. */
export const PLAN_PAYMENTS_PAGE_SIZE = 100;

/** The plan cards' add-action pill, styled like the Tasks card's Add Task. */
export const ACTION_BUTTON_CLASS =
  'shrink-0 inline-flex items-center gap-1 rounded-md bg-primary/15 px-2.5 py-1 text-xs font-medium text-primary hover:bg-primary/25 transition-colors';

// ─── Quotes ───────────────────────────────────────────────────────────────────

export function PlanQuotesSection({
  planId,
  patientId,
  patientName,
  enabled,
}: {
  planId: number;
  patientId: number;
  patientName: string;
  /** The patient's Quotes tab is visible. False sends no request. */
  enabled: boolean;
}) {
  const queryClient = useQueryClient();
  const [collapsed, setCollapsed] = useState(true);
  const [openQuoteId, setOpenQuoteId] = useState<number | null>(null);
  const [addError, setAddError] = useState<string | null>(null);

  // The patient's list, filtered here: it is small and unpaged, it already
  // carries the list columns (Balance included), and sharing its cache means a
  // change in either place refreshes both.
  const { data: patientQuotes, isError } = useQuery({
    queryKey: ['patient-quotes', patientId],
    queryFn: () => getQuotesByPatient(patientId),
    enabled,
    staleTime: 30_000,
  });
  useEffect(() => {
    if (isError) {
      toast.error("Could not load this plan's quotes. Refresh the page, and if it keeps happening, contact Prospera support.");
    }
  }, [isError]);

  const quotes = useMemo(
    () => (patientQuotes ?? [])
      .filter((q) => q.planId === planId)
      // Newest first, as legacy's list sorts on load (data-sortonload="desc").
      .sort((a, b) => (b.created ?? '').localeCompare(a.created ?? '') || b.quoteId - a.quoteId),
    [patientQuotes, planId],
  );

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['patient-quotes', patientId] });
  };

  const addMutation = useMutation({
    // The server takes the patient from the plan.
    mutationFn: () => createQuote({ planId }),
    onSuccess: (quote) => {
      setAddError(null);
      refresh();
      setOpenQuoteId(quote.quoteId);
    },
    onError: (err) => setAddError(getErrorMessage(err, 'Could not start a new quote. Try again. If it keeps happening, contact Prospera support.')),
  });

  // Hidden until the list arrives (no flash), but shown when this plan has no
  // quotes: the Add Quote button below is how a plan gets its first quote.
  if (!enabled || !patientQuotes) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors rounded-t-xl"
        onClick={() => setCollapsed((c) => !c)}
      >
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${collapsed ? '-rotate-90' : ''}`} />
        <FileText className="h-4 w-4 text-teal-500" />
        <h3 className="text-sm font-semibold text-slate-700">Quotes</h3>
        <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-teal-100 px-1.5 text-[10px] font-bold text-teal-600 leading-none">
          {quotes.length}
        </span>
      </button>

      {!collapsed && (
        <div className="space-y-2 px-4 pb-4">
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              className={ACTION_BUTTON_CLASS}
              onClick={() => addMutation.mutate()}
              disabled={addMutation.isPending}
            >
              <Plus className="h-3 w-3" />
              Add Quote
            </button>
          </div>

          {addError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{addError}</div>
          )}

          {quotes.length === 0 ? (
            <p className="text-xs text-slate-500">No quotes yet.</p>
          ) : (
          <div className="overflow-auto rounded-md border border-slate-100">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50 text-left text-[11px] font-medium text-slate-500">
                  <th className="px-3 py-2">Created</th>
                  <th className="px-3 py-2">Procedure(s)</th>
                  <th className="px-3 py-2 text-right">Effective Fee</th>
                  <th className="px-3 py-2 text-right">Anesthesia</th>
                  <th className="px-3 py-2 text-right">OR</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2 text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {quotes.map((quote) => (
                  <QuoteRow key={quote.quoteId} quote={quote} onOpen={() => setOpenQuoteId(quote.quoteId)} />
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      )}

      <QuoteEditorModal
        quoteId={openQuoteId}
        patientName={patientName}
        onClose={() => setOpenQuoteId(null)}
        onChanged={refresh}
        onOpenQuote={setOpenQuoteId}
      />
    </div>
  );
}

function QuoteRow({ quote, onOpen }: { quote: PatientQuoteRow; onOpen: () => void }) {
  const money = (amount: number | null) => (
    // Blank rather than $0.00 for a missing figure, as the Quotes tab does.
    amount === null ? '' : <span className="whitespace-nowrap">{formatCurrency(amount)}</span>
  );

  return (
    <tr
      className={cn(
        'border-b border-slate-50 last:border-0 hover:bg-slate-50/50 cursor-pointer',
        // Legacy marks the active quote bold and orange (#c94302), as the
        // Quotes tab does.
        quote.activeQuote && 'bg-orange-50/40 font-semibold [&>td]:text-[#c94302]',
      )}
      onClick={onOpen}
      data-testid={`plan-quote-row-${quote.quoteId}`}
    >
      <td className="px-3 py-2 whitespace-nowrap">
        {/* The row is clickable too, but only this reaches a keyboard. */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
          className="rounded text-left hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          {formatDate(quote.created)}
        </button>
      </td>
      <td className="px-3 py-2 text-slate-600">
        {quote.procedures.length > 0 ? quote.procedures.join(', ') : '—'}
      </td>
      <td className="px-3 py-2 text-right">{money(quote.effectiveFee)}</td>
      <td className="px-3 py-2 text-right">{money(quote.anesthesia)}</td>
      <td className="px-3 py-2 text-right">{money(quote.operatingRoom)}</td>
      <td className="px-3 py-2 text-right">{money(quote.total)}</td>
      {/* Legacy shows a balance only on the active quote; the server sends null otherwise. */}
      <td className="px-3 py-2 text-right" data-testid="plan-quote-balance">{money(quote.balance)}</td>
    </tr>
  );
}

// ─── Payments ─────────────────────────────────────────────────────────────────

function userName(usersById: Map<number, AdminUser>, userId: number | null): string | null {
  const user = userId ? usersById.get(userId) : null;
  return user ? `${user.firstName} ${user.lastName}`.trim() : null;
}

export function PlanPaymentsSection({
  planId,
  patientId,
  patientName,
  patientEmail,
  patientPhone,
  tz,
  enabled,
}: {
  planId: number;
  patientId: number;
  patientName: string;
  patientEmail: string | null;
  patientPhone: string | null;
  tz: string;
  /** The patient's Payments tab is visible. False sends no request. */
  enabled: boolean;
}) {
  const queryClient = useQueryClient();
  const [collapsed, setCollapsed] = useState(true);
  const [detailPaymentId, setDetailPaymentId] = useState<number | null>(null);
  const [chargeOpen, setChargeOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);

  const { data: page, isError } = useQuery({
    // Under the Payments tab's key, so a change saved on either side (the tab
    // invalidates ['patient-payments', patientId]) refreshes both.
    queryKey: ['patient-payments', patientId, 'plan', planId],
    // By plan alone, not patientId: a patientId list request also reconciles
    // the patient's terminal transactions (PaymentsController::index), which
    // the Payments tab does and a plan view should not repeat. planId already
    // names one plan, and the list is account-scoped server-side.
    queryFn: () => getPayments({ planId }, { page: 1, pageSize: PLAN_PAYMENTS_PAGE_SIZE }),
    enabled,
    staleTime: 30_000,
  });
  useEffect(() => {
    if (isError) {
      toast.error("Could not load this plan's payments. Refresh the page, and if it keeps happening, contact Prospera support.");
    }
  }, [isError]);

  const { data: users = [] } = useQuery<AdminUser[]>({
    queryKey: ['admin-users'],
    queryFn: listUsers,
    // Names only appear in the expanded table.
    enabled: enabled && !collapsed,
    staleTime: 5 * 60 * 1000,
  });
  const usersById = useMemo(() => new Map(users.map((u) => [u.userId, u])), [users]);

  const payments = page?.data ?? [];
  const total = page?.meta?.total ?? payments.length;

  const refresh = () => {
    // Also this card's own list, which sits under this prefix.
    void queryClient.invalidateQueries({ queryKey: ['patient-payments', patientId] });
    void queryClient.invalidateQueries({ queryKey: ['patient-payment-stats', patientId] });
    // The patient's Total Spend card is the same lifetime total.
    void queryClient.invalidateQueries({ queryKey: ['patient-sidebar-stats', patientId] });
    // A quote's Balance is its total less the plan's payments.
    void queryClient.invalidateQueries({ queryKey: ['patient-quotes', patientId] });
    // A payment can create plan tasks (the payment task trigger).
    void queryClient.invalidateQueries({ queryKey: ['plan-tasks', planId] });
    void queryClient.invalidateQueries({ queryKey: ['plan-task-counts', planId] });
  };

  // Hidden until the list arrives (no flash), but shown when it is empty: the
  // Add Payment button below is how a plan gets its first payment.
  if (!enabled || !page) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors rounded-t-xl"
        onClick={() => setCollapsed((c) => !c)}
      >
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${collapsed ? '-rotate-90' : ''}`} />
        <DollarSign className="h-4 w-4 text-sky-500" />
        <h3 className="text-sm font-semibold text-slate-700">Payments</h3>
        <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-sky-100 px-1.5 text-[10px] font-bold text-sky-600 leading-none">
          {total}
        </span>
      </button>

      {!collapsed && (
        <div className="space-y-2 px-4 pb-4">
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button type="button" className={ACTION_BUTTON_CLASS} onClick={() => setLinkOpen(true)}>
              Direct payment link
            </button>
            <button type="button" className={ACTION_BUTTON_CLASS} onClick={() => setChargeOpen(true)}>
              <Plus className="h-3 w-3" />
              Add Payment
            </button>
          </div>

          {payments.length === 0 ? (
            <p className="text-xs text-slate-500">No payments yet.</p>
          ) : (
          <div className="overflow-auto rounded-md border border-slate-100">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50 text-left text-[11px] font-medium text-slate-500">
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Amount</th>
                  <th className="px-3 py-2">Quote</th>
                  <th className="px-3 py-2">Materials</th>
                  <th className="px-3 py-2">Notes</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Entered By</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((row) => {
                  const giftCard = row.giftCardTotal ?? 0;
                  const enteredBy = userName(usersById, row.userID) ?? row.enteringUserName ?? null;
                  return (
                    <tr
                      key={row.paymentID}
                      className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 cursor-pointer"
                      onClick={() => setDetailPaymentId(row.paymentID)}
                      data-testid={`plan-payment-row-${row.paymentID}`}
                    >
                      <td className="px-3 py-2 whitespace-nowrap text-slate-700">{formatPaymentDate(row, tz)}</td>
                      <td className="px-3 py-2 text-slate-700 capitalize">{paymentTypeLabel(row)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {/* Charged amount, not the stored one — see PaymentsTab. */}
                        <span className="font-medium text-slate-900">{formatCurrency(row.cardChargedAmount ?? row.curAmount)}</span>
                        {giftCard > 0 && (
                          <span className="flex items-center gap-1 text-[11px] text-purple-600">
                            <Gift className="h-3 w-3 shrink-0" />
                            {formatCurrency(giftCard)}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-600" data-testid="plan-payment-quote">
                        {row.quoteID ? row.quoteID : ''}
                      </td>
                      <td className="px-3 py-2 text-slate-500">
                        <span className="block max-w-[200px] truncate" title={row.materialsText ?? undefined}>
                          {row.materialsText || '—'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-500">
                        <span className="block max-w-[160px] truncate" title={row.strMemo ?? undefined}>
                          {row.strMemo || '—'}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <PaymentStatusBadge
                          payment={row}
                          voidDate={row.voidDate}
                          voidUserName={userName(usersById, row.voidUserID)}
                        />
                      </td>
                      <td className="px-3 py-2 text-slate-500">{enteredBy ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}

          {total > payments.length && (
            <p className="text-[11px] text-slate-400">
              Showing the {payments.length} most recent payments. See Payments / Billing for the rest.
            </p>
          )}
        </div>
      )}

      {/* Mounted only while open: the charge modal loads its catalogs on
          mount, and a plan view should not pay for a popup nobody opened. */}
      {detailPaymentId !== null && (
        <RecordPatientPaymentModal
          patientId={patientId}
          patientName={patientName}
          paymentId={detailPaymentId}
          open
          onClose={() => setDetailPaymentId(null)}
          onSaved={refresh}
        />
      )}
      {chargeOpen && (
        <ChargePaymentModal
          patientId={patientId}
          patientName={patientName}
          planId={planId}
          open
          onClose={() => setChargeOpen(false)}
          onSaved={refresh}
        />
      )}
      <DirectPaymentLinkModal
        patientId={patientId}
        patientEmail={patientEmail}
        patientPhone={patientPhone}
        patientName={patientName}
        planId={planId}
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
      />
    </div>
  );
}
